import { useQuery, useMutation, useQueryClient } from 'react-query';
import ApiService from '@/services/api';
import { ISimListParams, ISimUpdateRequest } from '@/types/api';

// Query keys
export const SIM_QUERY_KEYS = {
  all: ['sims'] as const,
  lists: () => [...SIM_QUERY_KEYS.all, 'list'] as const,
  list: (params: ISimListParams) => [...SIM_QUERY_KEYS.lists(), params] as const,
  details: () => [...SIM_QUERY_KEYS.all, 'detail'] as const,
  detail: (iccid: string) => [...SIM_QUERY_KEYS.details(), iccid] as const,
  status: (iccid: string) => [...SIM_QUERY_KEYS.detail(iccid), 'status'] as const,
  connectivity: (iccid: string) => [...SIM_QUERY_KEYS.detail(iccid), 'connectivity'] as const,
};

// Hook for fetching SIM cards list
export const useSimCards = (params: ISimListParams = {}) => {
  return useQuery(
    SIM_QUERY_KEYS.list(params),
    () => ApiService.getSimCards(params),
    {
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
      cacheTime: 300000, // 5 minutes
    }
  );
};

// Hook for fetching single SIM card
export const useSimCard = (iccid: string) => {
  return useQuery(
    SIM_QUERY_KEYS.detail(iccid),
    () => ApiService.getSimCard(iccid),
    {
      enabled: !!iccid,
      staleTime: 60000, // 1 minute
    }
  );
};

// Hook for fetching SIM status
export const useSimStatus = (iccid: string) => {
  return useQuery(
    SIM_QUERY_KEYS.status(iccid),
    () => ApiService.getSimStatus(iccid),
    {
      enabled: !!iccid,
      refetchInterval: 30000, // Refresh every 30 seconds
      staleTime: 10000, // 10 seconds
    }
  );
};

// Hook for fetching SIM connectivity
export const useSimConnectivity = (
  iccid: string,
  params: { subscriber?: boolean; ussd?: boolean } = { subscriber: true }
) => {
  return useQuery(
    [...SIM_QUERY_KEYS.connectivity(iccid), params],
    () => ApiService.getSimConnectivity(iccid, params),
    {
      enabled: !!iccid,
      staleTime: 60000, // 1 minute
    }
  );
};

// Hook for updating SIM card
export const useUpdateSimCard = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ iccid, data }: { iccid: string; data: ISimUpdateRequest }) => 
      ApiService.updateSimCard(iccid, data),
    {
      onSuccess: (_, { iccid }) => {
        // Invalidate and refetch SIM card data
        queryClient.invalidateQueries(SIM_QUERY_KEYS.detail(iccid));
        queryClient.invalidateQueries(SIM_QUERY_KEYS.lists());
      },
    }
  );
};